import { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  AccessTime,
  LocationOn,
  CheckCircle,
  Cancel,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";
import { Chip } from "@mui/material";
import Avatar from "../components/ui/Avatar";
import Input from "../components/ui/Input";
import { CalendarToday } from "@mui/icons-material";

export default function Attendance() {
  const { user } = useAuth();
  const userId = useMemo(() => user?._id || user?.id, [user]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(
    () => new Date().toISOString().split("T")[0]
  );
  const [latestStatus, setLatestStatus] = useState(null);

  const fetchRecords = useCallback(async () => {
    if (!userId) {
      setRecords([]);
      setLatestStatus(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const dateResponse = await api.get("/attendance/logs", {
        params: {
          userId,
          date: selectedDate,
        },
      });

      const data = Array.isArray(dateResponse.data) ? dateResponse.data : [];
      setRecords(data);

      const todayString = new Date().toISOString().split("T")[0];
      let latest = data.length > 0 ? data[0] : null;

      if (!latest || selectedDate !== todayString) {
        const statusResponse = await api.get("/attendance/logs", {
          params: {
            userId,
            limit: 1,
          },
        });
        const statusData = Array.isArray(statusResponse.data)
          ? statusResponse.data
          : [];
        latest = statusData.length > 0 ? statusData[0] : latest;
      }

      setLatestStatus(latest);
    } catch (err) {
      console.error("Failed to fetch attendance logs", err);
      toast.error("Failed to load attendance records");
    } finally {
      setLoading(false);
    }
  }, [userId, selectedDate]);

  useEffect(() => {
    if (userId) {
      fetchRecords();
    }
  }, [userId, fetchRecords]);

  useEffect(() => {
    const handleAttendanceUpdate = () => {
      fetchRecords();
    };

    window.addEventListener("attendanceUpdated", handleAttendanceUpdate);
    return () =>
      window.removeEventListener("attendanceUpdated", handleAttendanceUpdate);
  }, [fetchRecords]);

  const openInMaps = (location) => {
    const { latitude, longitude } = location || {};
    if (!latitude || !longitude) return;
    const url = `https://www.google.com/maps?q=${latitude},${longitude}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const formatCoordinates = (location) => {
    const lat = Number(location?.latitude);
    const lng = Number(location?.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return null;
    }
    return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "--";
    return new Date(timestamp).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mb-6 sm:mb-8"
      >
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-primary mb-2">Attendance</h1>
        <p className="text-sm sm:text-base text-gray-600">Track your daily attendance records</p>
      </motion.div>

      {/* Filters and Latest Status */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="card mb-4 sm:mb-6"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <CalendarToday className="w-5 h-5 text-gray-400" />
              <label className="text-sm font-semibold text-gray-700">
                Select Date
              </label>
            </div>
            <Input
              type="date"
              value={selectedDate}
              onChange={(event) => setSelectedDate(event.target.value)}
            />
          </div>
          {latestStatus && (
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-4 border border-blue-100">
              <p className="text-sm font-semibold text-gray-700 mb-2">
                Latest Activity
              </p>
              <div className="flex items-center gap-3">
                <Chip
                  icon={
                    latestStatus.action === "checkin" ? (
                      <CheckCircle />
                    ) : (
                      <Cancel />
                    )
                  }
                  label={
                    latestStatus.action === "checkin"
                      ? "Checked In"
                      : "Checked Out"
                  }
                  color={
                    latestStatus.action === "checkin" ? "success" : "default"
                  }
                  size="small"
                  sx={{ borderRadius: 2 }}
                />
                <span className="text-sm text-gray-600">
                  {new Date(latestStatus.timestamp).toLocaleString()}
                </span>
              </div>
              {latestStatus.location?.address?.label && (
                <p className="text-xs text-gray-500 mt-2 line-clamp-1">
                  {latestStatus.location.address.label}
                </p>
              )}
            </div>
          )}
        </div>
      </motion.div>

      {/* Attendance Records */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="card">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 skeleton rounded-2xl"></div>
                <div className="w-20 h-6 skeleton rounded"></div>
              </div>
              <div className="h-4 skeleton rounded w-full mb-2"></div>
              <div className="h-4 skeleton rounded w-2/3"></div>
            </div>
          ))}
        </div>
      ) : records.length === 0 ? (
        <div className="card text-center py-12">
          <AccessTime className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg">
            No attendance activity for the selected date
          </p>
        </div>
      ) : (
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6"
        >
          {records.map((record, index) => (
            <motion.div
              key={record._id}
              variants={cardVariants}
              className="card group cursor-pointer"
              whileHover={{ y: -8, transition: { duration: 0.2 } }}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <Avatar
                      src={user?.profile_image}
                      name={user?.name || "U"}
                      size="md"
                      className="rounded-2xl"
                    />
                    <div
                      className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center text-white shadow-lg ${
                        record.action === "checkin"
                          ? "bg-gradient-to-br from-green-500 to-emerald-500"
                          : "bg-gradient-to-br from-orange-500 to-red-500"
                      }`}
                    >
                      {record.action === "checkin" ? (
                        <CheckCircle className="w-4 h-4" />
                      ) : (
                        <Cancel className="w-4 h-4" />
                      )}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-primary mb-1 capitalize">
                      {record.action === "checkin" ? "Check In" : "Check Out"}
                    </h3>
                    <p className="text-sm text-gray-500 flex items-center gap-1">
                      <AccessTime className="w-4 h-4" />
                      {formatTime(record.timestamp)}
                    </p>
                  </div>
                </div>
              </div>

              {record.location?.latitude && record.location?.longitude && (
                <div className="pt-4 border-t border-gray-100">
                  <button
                    onClick={() => openInMaps(record.location)}
                    className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors text-left"
                  >
                    <LocationOn className="w-5 h-5 text-blue-500" />
                    <div className="flex-1 min-w-0">
                      {record.location.address?.label ? (
                        <p className="text-sm font-medium text-gray-700 truncate">
                          {record.location.address.label}
                        </p>
                      ) : (
                        <p className="text-sm text-gray-500">View Location</p>
                      )}
                      {formatCoordinates(record.location) && (
                        <p className="text-xs text-gray-400 mt-0.5">
                          {formatCoordinates(record.location)}
                        </p>
                      )}
                    </div>
                  </button>
                </div>
              )}
            </motion.div>
          ))}
        </motion.div>
      )}
    </div>
  );
}
